import { useQuery } from '@tanstack/react-query';
import { TrendingUp, AlertTriangle } from 'lucide-react';
import { adminReports } from '@/api/admin';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fmtMoney } from '@/lib/format';
import type { Money } from '@/api/types';
import type { PlatformPnlPeriod } from '@/api/extras';
import { PerformancePanel } from '@/features/performance/PerformancePanel';

/**
 * 平台经营(只读)。三块口径**物理隔离**,前端绝不做跨区"合计":
 *  ① 真实成交台账(realAccountNet)—— 已成交净盈亏，用于核对成交记录。
 *  ② 商业模式指标 · 虚拟账本口径(服务费 / 分润)—— **非真实收入**,不可与 ① 相加。
 *  ③ 诊断 · 基差口径(basis / 滑点 / 佣金)—— **非盈亏**,不可与净盈亏相加。
 * 所有金额走 fmtMoney(Money value=分,÷10^digits=美元),不手动 /100。
 */
export function PlatformPnl() {
  const q = useQuery({
    queryKey: ['admin', 'platform-pnl'],
    queryFn: adminReports.platformPnl,
  });
  const data = q.data;

  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-brand" />
          平台经营
        </h1>
        <p className="text-sm text-ink-2 mt-1">
          真实账户多周期收益分析，附成交台账、商业模式指标与基差诊断。
        </p>
      </header>

      <PerformancePanel />

      {q.isLoading && <p className="text-sm text-ink-3">加载中…</p>}
      {q.isError && <p className="text-sm text-down">加载失败:{(q.error as Error)?.message}</p>}

      {data && (
        <>
          {/* ───── 区① 平台真实盈亏 ───── */}
          <section className="space-y-3">
            <div className="border-l-2 border-brand pl-3">
              <h2 className="text-base font-semibold text-ink-0">① 真实成交台账</h2>
              <p className="text-xs text-ink-2 mt-0.5">按真实成交记录汇总毛盈亏、佣金及库存费；不含未平仓浮盈变化，费用归属时间也可能与上方净值收益不同。</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <NetCard title="累计成交净盈亏" money={data.allTime.realAccountNet} />
              <NetCard title="今日成交净盈亏" money={data.today.realAccountNet} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <DetailCard title="累计 · 真实佣金" money={data.allTime.realCommission} sign />
              <DetailCard title="累计 · 真实库存费" money={data.allTime.realSwap} sign />
              <DetailCard title="今日 · 真实佣金" money={data.today.realCommission} sign />
              <DetailCard title="今日 · 真实库存费" money={data.today.realSwap} sign />
            </div>
          </section>

          {/* ───── 区② 商业模式指标 · 虚拟账本口径(非真实收入)───── */}
          <section className="space-y-3 pt-2">
            <div className="border-l-2 border-line pl-3">
              <h2 className="text-base font-semibold text-ink-0">② 商业模式指标 · 虚拟账本口径(非真实收入)</h2>
              <Callout>
                虚拟账本指标,衡量高频门槛 / 佣金覆盖;<span className="font-semibold">非平台真实收入,不可与真实盈亏相加</span>。
              </Callout>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <ServiceFeeCard
                title="服务费(用户名义 10× 尺度)"
                badge="用户名义 10× 尺度"
                pick={(p) => p.serviceFeeCollected}
                allTime={data.allTime}
                today={data.today}
              />
              <ServiceFeeCard
                title="服务费(折真实尺度 ×0.1)"
                badge="折真实尺度 ×0.1"
                pick={(p) => p.serviceFeeCollectedRealScale}
                allTime={data.allTime}
                today={data.today}
              />
              {/*
                口径提示:本卡走后端 platform-pnl 的 profitShare 块(ReportOptions::settled),
                该口径已随「月度分润」页默认视图一并剔除已归档人员与已停用渠道成员(连金额一起剔除),
                两处同口径可直接对账 —— 在卡上标明,免得再被当成两套账。
              */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-ink-2">平台当月分润</CardTitle>
                  <p className="text-[11px] leading-snug text-ink-3">
                    与「月度分润」页默认口径一致:已剔除已归档人员与已停用渠道成员
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-ink-0">{fmtMoney(data.profitShare.total)}</div>
                  <p className="text-xs text-ink-3 mt-1">
                    {data.profitShare.monthLabel} · {data.profitShare.settlements} 笔结算
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* ───── 区③ 诊断 · 基差口径(非盈亏)───── */}
          <section className="space-y-3 pt-2">
            <div className="border-l-2 border-line pl-3">
              <h2 className="text-base font-semibold text-ink-0">③ 诊断 · 基差口径(非盈亏)</h2>
              <Callout>
                对冲跟踪误差 / 诊断量,<span className="font-semibold">非平台盈亏,不可与净盈亏相加</span>;滑点上线后趋
                0,佣金为真实成本。仅累计,无当日维度。
              </Callout>
              <Callout>
                <span className="font-semibold">脱钩期基差不覆盖脱钩敞口</span>:执行「真实账户平仓」后,
                存量持仓被标记为已脱钩(不再对冲、平仓不发真实单、按中价结算),
                其结算按脱钩时点被排除在基差被减项之外 —— 否则基差会单调发散,把平台的账外负债显示成收益。
                <span className="font-semibold">基差回到 0 附近不代表敞口已平</span>,
                脱钩期的真实风险规模看下方「未对冲敞口」两项。
              </Callout>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <DetailCard title="累计基差" money={data.diagnostics.basis} sign />
              <DetailCard title="基差滑点(basis − 佣金 − 库存费)" money={data.diagnostics.basisSlippage} sign />
              <DetailCard title="基差佣金(= 真实佣金)" money={data.diagnostics.basisCommission} sign />
            </div>

            {/* 未对冲敞口科目:基差管不到的那一块。非盈亏,更不可与 ① 相加 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <DetailCard
                title="未对冲敞口 · 脱钩持仓毛名义"
                money={data.diagnostics.unhedgedVirtualExposure}
                hint="平台此刻裸承担的敞口规模(非盈亏);无脱钩持仓时为 0"
              />
              <DetailCard
                title="未对冲敞口 · 脱钩持仓用户浮盈"
                money={data.diagnostics.unhedgedUserFloating}
                sign
                hint="用户口径浮动盈亏;用户浮盈 = 平台待赔,是账外负债预估(非盈亏)"
              />
            </div>
            <p className="text-xs text-ink-3">
              两项均在当晚兜底结算后归零(脱钩持仓仍参与当日结算,脱钩态生命周期上界 = 一个交易日);
              后端未返回该科目时显示 —,不按 0 解读。
            </p>
          </section>
        </>
      )}
    </div>
  );
}

/** 区②③ 顶部强标注文字。 */
function Callout({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs text-warn mt-1 flex items-start gap-1.5">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
      <span>{children}</span>
    </p>
  );
}

/** 区① 大数卡:涨绿跌红按 Money.value 正负(realAccountNet)。 */
function NetCard({ title, money }: { title: string; money: Money }) {
  const positive = Number(money.value) >= 0;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-ink-2">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-3xl font-bold ${positive ? 'text-up' : 'text-down'}`}>
          {fmtMoney(money, { sign: true })}
        </div>
        <p className="text-xs text-ink-3 mt-1">真实成交毛盈亏及已记录费用合计</p>
      </CardContent>
    </Card>
  );
}

/**
 * 明细小卡(佣金 / 库存费 / 基差 / 未对冲敞口等),金额带正负号但不染色。
 * money 允许缺省:后端灰度中未返回的科目显示 —,不回退成 0(0 会被误读成"没有敞口")。
 */
function DetailCard({
  title,
  money,
  sign,
  hint,
}: {
  title: string;
  money: Money | null | undefined;
  sign?: boolean;
  hint?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-ink-2">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-xl font-bold text-ink-0">{fmtMoney(money, { sign })}</div>
        {hint && <p className="text-xs text-ink-3 mt-1">{hint}</p>}
      </CardContent>
    </Card>
  );
}

/** 区② 服务费卡:同时展示 allTime / today(均为非真实收入)。 */
function ServiceFeeCard({
  title,
  badge,
  pick,
  allTime,
  today,
}: {
  title: string;
  badge: string;
  pick: (p: PlatformPnlPeriod) => Money;
  allTime: PlatformPnlPeriod;
  today: PlatformPnlPeriod;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-ink-2">{title}</CardTitle>
        <span className="inline-block w-fit rounded-md bg-bg-3 px-1.5 py-0.5 text-[10px] font-medium text-ink-3">
          {badge}
        </span>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-ink-0">{fmtMoney(pick(allTime))}</div>
        <p className="text-xs text-ink-3 mt-1">累计 · 今日 {fmtMoney(pick(today))}</p>
      </CardContent>
    </Card>
  );
}
